/**
 * Browser-back and page-refresh guard (Safari / WebKit aware).
 *
 * Adapted for React + BrowserRouter:
 * - Back: history trap via unique hash (#uidgN) that RR ignores, + custom modal on popstate
 * - On popstate we restore the protected URL *before* React Router reads location (capture phase)
 * - Refresh: beforeunload (browser confirm) + custom modal on F5 / Ctrl+R / Cmd+R
 *
 * Always call disableBackNavigationGuard() before intentional external navigation.
 */

const GUARD_THROTTLE_MS = 300;
const EDGE_PX = 45;
export const BACK_GUARD_RESTORE_EVENT = 'uid:back-guard-restore';

let lastGesturePushAt = 0;
let seq = 0;
let listenersBound = false;
let protectedPathname = '';
let protectedSearch = '';

export let allowLeavePage = false;
export let backNavigationGuardEnabled = false;

type GuardUi = {
  showBackWarning: () => void;
  hideBackWarning: () => void;
  showRefreshWarning: () => void;
  hideRefreshWarning: () => void;
};

let guardUi: GuardUi = {
  showBackWarning: () => {
    const modal = document.getElementById('backNavigationWarningModal');
    if (modal) modal.style.display = 'block';
  },
  hideBackWarning: () => {
    const modal = document.getElementById('backNavigationWarningModal');
    if (modal) modal.style.display = 'none';
  },
  showRefreshWarning: () => {
    const modal = document.getElementById('refreshWarningModal');
    if (modal) modal.style.display = 'block';
  },
  hideRefreshWarning: () => {
    const modal = document.getElementById('refreshWarningModal');
    if (modal) modal.style.display = 'none';
  },
};

/** Optional leave handler (e.g. partner redirect). Defaults to history.go(-1). */
let onLeavePage: (() => void) | null = null;

export function registerBackNavigationGuardUi(ui: Partial<GuardUi>) {
  guardUi = { ...guardUi, ...ui };
}

export function registerLeavePageHandler(handler: (() => void) | null) {
  onLeavePage = handler;
}

/** Call whenever the SPA route that should be protected changes. */
export function rememberProtectedLocation(pathname?: string, search?: string) {
  protectedPathname = pathname ?? window.location.pathname;
  protectedSearch = search ?? window.location.search;
}

function guardUrl() {
  seq += 1;
  // Always use remembered protected route — on popstate window.location is already previous.
  return protectedPathname + protectedSearch + '#uidg' + seq;
}

export function pushGuard(tag?: string) {
  if (!protectedPathname) {
    rememberProtectedLocation();
  }
  try {
    window.history.pushState(
      { uidBackGuard: true, tag: tag || '', n: seq + 1 },
      document.title,
      guardUrl(),
    );
    lastGesturePushAt = Date.now();
  } catch {
    // Some WebViews throw on pushState
  }
}

function restoreProtectedHistory(tag: string) {
  if (!protectedPathname) {
    rememberProtectedLocation();
  }
  try {
    window.history.pushState(
      { uidBackGuard: true, tag, n: seq + 1 },
      document.title,
      guardUrl(),
    );
    lastGesturePushAt = Date.now();
  } catch {
    // Some WebViews throw on pushState
  }
}

function onGesture() {
  if (allowLeavePage || !backNavigationGuardEnabled) return;
  const now = Date.now();
  if (now - lastGesturePushAt < GUARD_THROTTLE_MS) return;
  pushGuard('gesture');
}

function isInteractiveTouchTarget(target: EventTarget | null) {
  if (!target) return false;
  let el = target as Node | null;
  if (el && el.nodeType !== 1) {
    el = (el as Node).parentElement;
  }
  if (!el || !(el instanceof Element)) return false;

  if (typeof el.closest === 'function') {
    return !!el.closest('input, button, select, textarea, a, label, [role="button"]');
  }

  let node: Element | null = el;
  while (node && node !== document.documentElement) {
    const tag = node.tagName;
    if (
      tag === 'INPUT' ||
      tag === 'BUTTON' ||
      tag === 'SELECT' ||
      tag === 'TEXTAREA' ||
      tag === 'A' ||
      tag === 'LABEL'
    ) {
      return true;
    }
    node = node.parentElement;
  }
  return false;
}

function onEdgeSwipeBlock(event: TouchEvent) {
  if (allowLeavePage || !backNavigationGuardEnabled) return;
  if (!event.touches || event.touches.length !== 1) return;
  if (isInteractiveTouchTarget(event.target)) return;
  const x = event.touches[0].clientX;
  if (x < EDGE_PX || x > window.innerWidth - EDGE_PX) {
    event.preventDefault();
  }
}

/**
 * Capture phase so we restore the URL before React Router's bubble-phase popstate handler
 * reads window.location and switches routes.
 */
function onPopState() {
  if (allowLeavePage) return;

  restoreProtectedHistory('popstate');

  window.dispatchEvent(
    new CustomEvent(BACK_GUARD_RESTORE_EVENT, {
      detail: { pathname: protectedPathname, search: protectedSearch },
    }),
  );

  showBackNavigationWarning();
}

function onPageShow(event: PageTransitionEvent) {
  if (allowLeavePage) return;
  if (event.persisted) {
    lastGesturePushAt = 0;
    enableBackNavigationGuard();
  }
}

function isRefreshShortcut(event: KeyboardEvent) {
  if (event.key === 'F5') return true;
  const key = event.key;
  if (typeof key !== 'string') return false;
  return (event.ctrlKey || event.metaKey) && key.toLowerCase() === 'r';
}

function onRefreshKeydown(event: KeyboardEvent) {
  if (allowLeavePage || !backNavigationGuardEnabled) return;
  if (!isRefreshShortcut(event)) return;
  event.preventDefault();
  showRefreshWarning();
}

function onBeforeUnload(event: BeforeUnloadEvent) {
  if (allowLeavePage || !backNavigationGuardEnabled) return;
  event.preventDefault();
  event.returnValue = '';
  return '';
}

function bindListeners() {
  if (listenersBound) return;
  listenersBound = true;
  // capture: true — run before React Router's popstate listener
  window.addEventListener('popstate', onPopState, true);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('beforeunload', onBeforeUnload);
  document.addEventListener('keydown', onRefreshKeydown, true);
  document.addEventListener('pointerdown', onGesture, true);
  document.addEventListener('touchstart', onGesture, true);
  document.addEventListener('click', onGesture, true);
  document.addEventListener('touchstart', onEdgeSwipeBlock, {
    capture: true,
    passive: false,
  });
}

function unbindListeners() {
  if (!listenersBound) return;
  listenersBound = false;
  window.removeEventListener('popstate', onPopState, true);
  window.removeEventListener('pageshow', onPageShow);
  window.removeEventListener('beforeunload', onBeforeUnload);
  document.removeEventListener('keydown', onRefreshKeydown, true);
  document.removeEventListener('pointerdown', onGesture, true);
  document.removeEventListener('touchstart', onGesture, true);
  document.removeEventListener('click', onGesture, true);
  document.removeEventListener('touchstart', onEdgeSwipeBlock, true);
}

export function showBackNavigationWarning() {
  guardUi.showBackWarning();
}

export function showRefreshWarning() {
  guardUi.showRefreshWarning();
}

export function closeRefreshWarning() {
  guardUi.hideRefreshWarning();
}

export function enableBackNavigationGuard() {
  allowLeavePage = false;
  backNavigationGuardEnabled = true;
  (window as any).allowLeavePage = false;
  (window as any).backNavigationGuardEnabled = true;
  bindListeners();
}

export function disableBackNavigationGuard() {
  allowLeavePage = true;
  backNavigationGuardEnabled = false;
  (window as any).allowLeavePage = true;
  (window as any).backNavigationGuardEnabled = false;
  unbindListeners();
  closeRefreshWarning();
  guardUi.hideBackWarning();
}

export function stayOnVerificationPage() {
  guardUi.hideBackWarning();
  if (!allowLeavePage) {
    pushGuard('stay');
  }
}

export function leaveVerificationPage() {
  guardUi.hideBackWarning();
  disableBackNavigationGuard();
  if (onLeavePage) {
    onLeavePage();
    return;
  }
  const redirectUrl = localStorage.getItem('user_redirect_url');
  if (redirectUrl) {
    window.location.href = redirectUrl;
    return;
  }
  window.history.go(-1);
}

/**
 * Disable the guard then navigate away. Use for all intentional external redirects.
 */
export function navigateAway(url: string) {
  disableBackNavigationGuard();
  window.location.href = url;
}

/**
 * Disable the guard then reload. Use when the app intentionally reloads.
 */
export function reloadPage() {
  disableBackNavigationGuard();
  window.location.reload();
}

(window as any).enableBackNavigationGuard = enableBackNavigationGuard;
(window as any).disableBackNavigationGuard = disableBackNavigationGuard;
(window as any).showBackNavigationWarning = showBackNavigationWarning;
(window as any).showRefreshWarning = showRefreshWarning;
(window as any).closeRefreshWarning = closeRefreshWarning;
(window as any).stayOnVerificationPage = stayOnVerificationPage;
(window as any).leaveVerificationPage = leaveVerificationPage;
(window as any).navigateAway = navigateAway;
